import { FormColumns, FormLabel } from "components/form";
import {
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "ui/components/form";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "ui/components/dialog";
import { Button } from "ui/components/button";
import SelectName from "components/select/name";
import ButtonWithLoading from "@/components/button-with-loading";
import { Checkbox } from "ui/components/checkbox";
import { DatePicker } from "components/date-picker-2";
import EmployeeInput from "components/employee/input";
import { Form } from "react-crud-form";
import { Input } from "ui/components/input";
import { Label } from "ui/components/label";
import { PermissionControlledInput } from "permissions/components/input";
import { PermissionGate } from "permissions/components/gate";
import { StatusTabInput } from "components/status/tabs/input";
import React, { useState } from "react";
import { Textarea } from "ui/components/textarea";
import { useHotkeys } from "react-hotkeys-hook";
import { useJobForm } from "./useJobForm";
import { useNavigate } from "react-router-dom";
import { useParams } from "react-router-dom";
import { FormActions } from "components/form/actions";
import { ROUTE } from "@/lib/utils";
import HeaderPortal from "components/portal/header-portal";
import TagsFilterCombobox from "components/directus/combobox/tags-filter";

const JobForm = ({ defaultProjectId, type, config }) => {
  const { jobId } = useParams();
  const navigate = useNavigate();

  const {
    form,
    requiredFields,
    handleCreate,
    handleRead,
    handleUpdate,
    milestoneCount,
    handleArchive,
    handleCancel,
    handleClose,
  } = useJobForm({ defaultProjectId, type });

  const selectedProject = form.watch("project");
  const selectedType = type || form.watch("type");

  const status = form.watch("status");
  const isStatusDisabled =
    status === "in_review" ||
    (status === "draft" && !config) ||
    status === "closed" ||
    status === "archived" ||
    status === "cancelled";

  // Free jobs in draft status are view-only; updates are blocked until status moves forward
  const isFreeJobDraft = (type === "free" || selectedType === "free") && status === "draft";


  const [confirmation, setConfirmation] = useState({
    open: false,
    title: "",
    description: "",
    action: null,
  });
  const [actionLoading, setActionLoading] = useState(false);

  const handleConfirm = async () => {
    if (!confirmation.action) return;
    setActionLoading(true);
    try {
      await confirmation.action();
      setConfirmation({ ...confirmation, open: false });
      navigate(ROUTE.JOB(jobId));
    } catch (error) {
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <Form
      form={form}
      create={handleCreate}
      read={handleRead}
      update={handleUpdate}
      itemId={jobId || "+"}
    >
      {({
        isCreating,
        createItem,
        isNewItem,
        isUpdating,
        updateItem,
        isDirty,
      }) => {
        useHotkeys(
          "ctrl+s, cmd+s",
          (event) => {
            event.preventDefault();
            createItem();
          },
          { preventDefault: true },
        );

        return (
          <div className="space-y-6">
            <FormField
              control={form.control}
              name="status"
              render={({ field }) => (
                <FormItem>
                  <PermissionControlledInput
                    rule={isNewItem ? "create:PC,SC,AM" : "update:PC,SC,JC,AM"}
                    collection="jobs"
                    type="form"
                    isDisabled={isStatusDisabled && !isFreeJobDraft}
                  >
                    <FormLabel required={requiredFields.includes(field.name)}>
                      Status
                    </FormLabel>
                    <StatusTabInput
                      {...field}
                      remove={["closed", "archived", "cancelled"]}
                      collection="jobs"
                    />
                  </PermissionControlledInput>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <PermissionControlledInput
                    rule={isNewItem ? "create:PC,SC,AM" : "update:PC,SC,JC,AM"}
                    collection="jobs"
                    field={field.name}
                    type="form"
                  >
                    <FormLabel required={requiredFields.includes(field.name)}>
                      Name
                    </FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                  </PermissionControlledInput>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="coordinator"
              render={({ field }) => (
                <FormItem>
                  <PermissionControlledInput
                    rule={isNewItem ? "create:PC,SC,AM" : "update:PC,SC,AM"}
                    collection="jobs"
                    field={field.name}
                    type="form"
                    disabledMessage="Only Project Coordinators can change the job coordinator"
                  >
                    <FormLabel required={requiredFields.includes(field.name)}>
                      Coordinator
                    </FormLabel>
                    <EmployeeInput set="jobs_coordinating" {...field} />
                  </PermissionControlledInput>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Start Date and End Date - PC, SC, and JC can update */}
            <FormColumns>
              <FormField
                control={form.control}
                name="start_date"
                render={({ field }) => (
                  <FormItem>
                    <PermissionControlledInput
                      rule={isNewItem ? "create:PC,SC,AM" : "update:none"}
                      collection="jobs"
                      field={field.name}
                      type="form"
                    >
                      <FormLabel required={requiredFields.includes(field.name)}>
                        Start Date
                      </FormLabel>
                      <DatePicker {...field} />
                    </PermissionControlledInput>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="end_date"
                render={({ field }) => (
                  <FormItem>
                    <PermissionControlledInput
                      rule={isNewItem ? "create:PC,SC,AM" : "update:none"}
                      collection="jobs"
                      field={field.name}
                      type="form"
                    >
                      <FormLabel required={requiredFields.includes(field.name)}>
                        End Date
                      </FormLabel>
                      <DatePicker {...field} />
                    </PermissionControlledInput>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </FormColumns>

            <FormField
              control={form.control}
              name="contract"
              render={({ field }) => (
                <FormItem>
                  <PermissionControlledInput
                    rule={isNewItem ? "create:PC,SC,AM" : "update:PC,SC,AM"}
                    collection="jobs"
                    field={field.name}
                    type="form"
                  >
                    <FormLabel required={requiredFields.includes(field.name)}>
                      Contract
                    </FormLabel>
                    <SelectName
                      collection="contracts"
                      filter={{
                        _and: [
                          {
                            project: {
                              id: {
                                _eq: selectedProject || defaultProjectId,
                              },
                            },
                          },
                          {
                            type: {
                              _eq: selectedType || type,
                            },
                          },
                          {
                            status: {
                              _nin: ["cancelled"],
                            },
                          },
                        ],
                      }}
                      fetchItems={true}
                      value={field.value}
                      onValueChange={(val) => field.onChange(val)}
                      {...field}
                    />
                  </PermissionControlledInput>
                  <FormMessage />
                </FormItem>
              )}
            />

            {selectedType !== "free" && (
              <FormField
                control={form.control}
                name="margin_calculator_link"
                render={({ field }) => (
                  <FormItem>
                    <PermissionControlledInput
                      rule={isNewItem ? "create:SC,AM" : "update:SC,AM"}
                      collection="jobs"
                      disabledMessage="Only Sales Coordinator, Account Manager and Admin can edit the margin calculator link"
                      field={field.name}
                      type="form"
                    >
                      <FormLabel required={requiredFields.includes(field.name)}>
                        Margin Calculator Link
                      </FormLabel>
                      <FormControl>
                        <Input
                          type="search"
                          autoComplete="nope"
                          data-1p-ignore
                          data-lpignore="true"
                          data-bwignore="true"
                          className="[&::-webkit-search-cancel-button]:hidden"
                          {...field} />
                      </FormControl>
                    </PermissionControlledInput>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <PermissionControlledInput
                    rule={isNewItem ? "create:PC,SC,AM" : "update:PC,SC,JC,AM"}
                    collection="jobs"
                    field={field.name}
                    type="form"
                  >
                    <FormLabel required={requiredFields.includes(field.name)}>
                      Description
                    </FormLabel>
                    <FormControl>
                      <Textarea {...field} className="min-h-32" />
                    </FormControl>
                  </PermissionControlledInput>
                  <FormMessage />
                </FormItem>
              )}
            />


            <FormField
              control={form.control}
              name="tags"
              render={({ field }) => (
                <FormItem>
                  <PermissionControlledInput
                    rule={isNewItem ? "create:PC,SC,AM" : "update:PC,SC,JC,AM"}
                    collection="jobs"
                    field={field.name}
                    type="form"
                  >
                    <FormLabel required={requiredFields.includes(field.name)}>
                      Tags
                    </FormLabel>
                    <TagsFilterCombobox
                      for="jobs"
                      isMulti
                      hideLabel
                      values={field.value}
                      onValuesChange={field.onChange}
                      value={field.value}
                      onChange={field.onChange}
                    />
                  </PermissionControlledInput>
                  <FormMessage />
                </FormItem>
              )}
            />


            {selectedType !== "hourly" || selectedType !== "bucket" && (
              <FormField
                control={form.control}
                name="disable_job_logging"
                render={({ field }) => (
                  <FormItem>
                    <PermissionControlledInput
                      rule={isNewItem ? "create:none" : "update:PC,SC,JC,AM"}
                      collection="jobs"
                      field={field.name}
                      className="flex gap-3 items-start"
                      disabled={milestoneCount === 0}
                    >
                      <Checkbox
                        id="disable-job-logging"
                        checked={field.value}
                        onCheckedChange={(checked) => field.onChange(checked)}
                        disabled={milestoneCount === 0}
                      />
                      <div className="grid gap-2">
                        <Label
                          htmlFor="disable-job-logging"
                          className="opacity-70"
                          disabled={milestoneCount === 0}
                        >
                          Disable Job Logging
                        </Label>
                        <p className="text-muted-foreground text-sm">
                          When enabled, employees can log timesheets only at the
                          milestone level. To use this option, create at least one
                          milestone first.
                        </p>
                      </div>
                    </PermissionControlledInput>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            <FormActions
              isNewItem={isNewItem}
              isCreating={isCreating}
              isUpdating={isUpdating}
              isDirty={isDirty}
              createItem={createItem}
              updateItem={() => updateItem().then(() => navigate(-1))}
              rule={isNewItem ? "create:PC,SC,AM" : "update:PC,SC,JC,AM"}
              collection="jobs"
              teleport={true}
            />

            {!isNewItem && (
              <HeaderPortal>
                <PermissionGate
                  rule={"update:PC,SC,JC,AM"}
                  collection="jobs"
                  fallback={
                    <div className="text-sm text-gray-500 italic">
                      You don't have permission to update this job
                    </div>
                  }
                >
                  <div className="flex gap-2">

                    <PermissionControlledInput
                      rule={
                        status === "draft"
                          ? "update:Admin,AM"
                          : "update:PC,SC,JC,AM"
                      }
                      collection="jobs"
                    >
                      <ButtonWithLoading
                        onClick={() =>
                          setConfirmation({
                            open: true,
                            title: "Cancel this Job Permanently",
                            description:
                              "This action will permanently cancel this job. You will NOT be able to edit, reopen, or log time. All future actions on this job will be blocked. This action cannot be undone.",
                            action: handleCancel,
                          })
                        }
                        disabled={
                          status === "closed" ||
                          status === "cancelled" ||
                          isUpdating
                        }
                        variant="outline"
                      >
                        {status === "cancelled"
                          ? "Cancelled"
                          : "Cancel this job"}
                      </ButtonWithLoading>
                    </PermissionControlledInput>

                    <PermissionControlledInput
                      rule={
                        status === "draft"
                          ? "update:Admin,AM"
                          : "update:PC,SC,JC,AM"
                      }
                      collection="jobs"
                    >
                      <ButtonWithLoading
                        onClick={() =>
                          setConfirmation({
                            open: true,
                            title: "Close this Job",
                            description:
                              "This action will close this job and mark it as completed. No new time entries or updates can be made. Existing data and history will remain available. The job can be reopened later if required. This action does not delete any data.",
                            action: handleClose,
                          })
                        }
                        disabled={status === "closed" || isUpdating}
                        variant="outline"
                      >
                        {status === "closed" ? "Closed" : "Close this job"}
                      </ButtonWithLoading>
                    </PermissionControlledInput>
                  </div>
                </PermissionGate>
              </HeaderPortal>)}

            <Dialog
              open={confirmation.open}
              onOpenChange={(open) =>
                setConfirmation((prev) => ({ ...prev, open }))
              }
            >
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>{confirmation.title}</DialogTitle>
                  <DialogDescription className="text-red-500">
                    {confirmation.description}
                  </DialogDescription>
                </DialogHeader>
                <DialogFooter className="gap-2">
                  <Button
                    variant="outline"
                    onClick={() =>
                      setConfirmation((prev) => ({ ...prev, open: false }))
                    }
                  >
                    Cancel
                  </Button>
                  <ButtonWithLoading
                    onClick={handleConfirm}
                    isLoading={actionLoading}
                    variant="default"
                  >
                    Confirm
                  </ButtonWithLoading>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        );
      }}
    </Form>
  );
};

export default JobForm;